/**
 * What a hot streak sheds off its way (the client's `render/fx/streak.ts`, the admin app's Skins page): motes, each a
 * small white mask (`MOTES`) tinted as it goes, moved by this one engine (`spawnMote`, `stepMote`) and drawn by whoever
 * shows it from `viewMote`, never moved in a renderer of its own. A plain gun's embers, the Freeze Gun's frost, the
 * Homing Gun's sparks and every effect skin's own: each skin its own shape (a test keeps them apart).
 */

const unit = (v: number) => Math.max(0, Math.min(1, v));

/** A mask's pixel (x, y) from its middle: across, down, how far out, at what angle. */
const polar = (x: number, y: number, w: number, h: number) => {
  const [px, py] = [x + 0.5 - w / 2, y + 0.5 - h / 2];
  return { px, py, r: Math.hypot(px, py), t: Math.atan2(py, px) };
};

/** How far point (x, y) is from the polyline `pts` ([x, y] pairs). */
function toLine(x: number, y: number, pts: readonly (readonly [number, number])[]): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1] as readonly [number, number];
    const [bx, by] = pts[i] as readonly [number, number];
    const [dx, dy] = [bx - ax, by - ay];
    const k = unit(((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy));
    best = Math.min(best, Math.hypot(x - ax - dx * k, y - ay - dy * k));
  }
  return best;
}

/** A tempest's bolt, a zigzag across its mask. */
const ZIGZAG = [
  [-13, 1],
  [-7, -4],
  [-2, 3],
  [4, -4],
  [9, 2],
  [13, -1],
] as const;

/** A music note's flag, from the top of its stem curling down. */
const FLAG = [
  [1, -13],
  [5, -10],
  [8, -6],
  [8, -2],
] as const;

export interface MoteMask {
  w: number;
  h: number;
  /** Its alpha at pixel (x, y), 0 … 1 (clamped). */
  a: (x: number, y: number) => number;
  /** How it turns as drawn: never, along its flight, round and round, or end over end (a coin's flip). */
  turn: 'up' | 'way' | 'spin' | 'flip';
  /** A solid bit (paper, confetti, a coin, pepper): drawn as it is, not added on as light. */
  solid?: true;
  /** Longer the faster it flies (a spark, a drop). */
  stretch?: true;
  /** Wobbles like jelly (a blob of gloop). */
  squash?: true;
}

/** Every mote's mask, white: tinted where it is drawn. */
export const MOTES = {
  /** A plain gun's ember, the Homing Gun's spark. */
  dot: { w: 32, h: 32, turn: 'up', a: (x, y) => 15 - polar(x, y, 32, 32).r },
  /** The Freeze Gun's frost: a thin shard of ice. */
  shard: {
    w: 32,
    h: 12,
    turn: 'spin',
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 12);
      return (1 - Math.abs(px) / 15 - Math.abs(py) / 5) * 3;
    },
  },
  /** A bubble: a thin rim, a faint film and a glint up on its left. */
  bubble: {
    w: 32,
    h: 32,
    turn: 'up',
    a: (x, y) => {
      const { px, py, r } = polar(x, y, 32, 32);
      return Math.max(2.2 - Math.abs(r - 12), r < 12 ? 0.12 : 0, 3 - Math.hypot(px + 5, py + 5));
    },
  },
  /** A feather: a vane with a notched edge either side of its quill. */
  feather: {
    w: 32,
    h: 16,
    turn: 'spin',
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 16);
      const vane = unit(5.5 * (1 - ((px - 2) / 15) ** 2) - Math.abs(py)) * (0.68 + 0.1 * Math.sin(px * 1.6));
      return Math.max(vane, Math.abs(py) < 0.9 && Math.abs(px) < 15 ? 1 : 0);
    },
  },
  /** A ball of plasma: a hot core in a soft halo. */
  orb: {
    w: 32,
    h: 32,
    turn: 'up',
    a: (x, y) => {
      const { r } = polar(x, y, 32, 32);
      return Math.max(5.5 - r, 0.55 * Math.exp(-((r / 8) ** 2)));
    },
  },
  /** A drop of water, its round end leading. */
  drop: {
    w: 32,
    h: 20,
    turn: 'way',
    stretch: true,
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 20);
      const tail = px < 6 && px > -14 ? 7 * ((px + 14) / 20) ** 1.3 - Math.abs(py) : 0;
      return Math.max(7 - Math.hypot(px - 6, py), tail);
    },
  },
  /** A tongue of flame, licking upwards. */
  flame: {
    w: 20,
    h: 32,
    turn: 'up',
    a: (x, y) => {
      const { px, py } = polar(x, y, 20, 32);
      const tongue = py < 7 && py > -15 ? 8 * ((py + 15) / 22) ** 0.8 - Math.abs(px - Math.sin(py * 0.35) * 1.5) : 0;
      return Math.max(8 - Math.hypot(px, py - 7), tongue);
    },
  },
  /** A wisp of foxfire: a bright head trailing a fading tail. */
  wisp: {
    w: 32,
    h: 12,
    turn: 'way',
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 12);
      const k = (px + 16) / 25;
      return Math.max(4.5 - Math.hypot(px - 9, py), px < 9 ? unit(3.5 * k - Math.abs(py)) * k ** 1.2 : 0);
    },
  },
  /** A vortex: two arms winding into a bright eye. */
  vortex: {
    w: 32,
    h: 32,
    turn: 'spin',
    a: (x, y) => {
      const { r, t } = polar(x, y, 32, 32);
      const arm = (0.5 + 0.5 * Math.cos(2 * t - r * 0.45) - 0.35) * 2.5;
      return Math.max(unit(arm) * unit((14 - r) / 4) * unit(r / 2), 2.5 - r);
    },
  },
  /** A bolt of lightning, zigzagging. */
  bolt: {
    w: 32,
    h: 16,
    turn: 'spin',
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 16);
      const d = toLine(px, py, ZIGZAG);
      return Math.max(1.7 - d, 0.35 * Math.exp(-((d / 3) ** 2)));
    },
  },
  /** A snowflake: six arms round a bright middle. */
  flake: {
    w: 32,
    h: 32,
    turn: 'spin',
    a: (x, y) => {
      const { r, t } = polar(x, y, 32, 32);
      const sixth = Math.PI / 3;
      const off = (((t % sixth) + sixth) % sixth) - sixth / 2;
      const arm = 1.4 - r * Math.abs(Math.sin(sixth / 2 - Math.abs(off)));
      return Math.max(unit(arm) * unit((14 - r) / 2), 2.5 - r);
    },
  },
  /** A ripple: a flat ring. */
  ripple: {
    w: 32,
    h: 16,
    turn: 'up',
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 16);
      return 1 - Math.abs(Math.hypot(px / 14, py / 7) - 0.85) * 9;
    },
  },
  /** A harlequin's diamond. */
  diamond: {
    w: 24,
    h: 32,
    turn: 'spin',
    solid: true,
    a: (x, y) => {
      const { px, py } = polar(x, y, 24, 32);
      return (1 - Math.abs(px) / 11 - Math.abs(py) / 15) * 6;
    },
  },
  /** A puff of smoke: smoke hides what is behind it, it never glows. */
  puff: { w: 32, h: 32, turn: 'up', solid: true, a: (x, y) => 1.2 * Math.exp(-((polar(x, y, 32, 32).r / 9) ** 2)) },
  /** A cog: a ring with eight teeth round a hole. */
  gear: {
    w: 32,
    h: 32,
    turn: 'spin',
    a: (x, y) => {
      const { r, t } = polar(x, y, 32, 32);
      const ring = Math.min(r - 3.5, 10.5 - r) * 1.5;
      const tooth = r >= 10 ? Math.min(13.8 - r, (Math.cos(8 * t) - 0.2) * 6) : 0;
      return Math.max(ring, tooth);
    },
  },
  /** A pixel, square and solid. */
  pixel: { w: 8, h: 8, turn: 'up', solid: true, a: () => 1 },
  /** A music note (♪). */
  note: {
    w: 24,
    h: 32,
    turn: 'up',
    a: (x, y) => {
      const { px, py } = polar(x, y, 24, 32);
      const [c, s] = [Math.cos(0.35), Math.sin(0.35)];
      const [qx, qy] = [(px + 4) * c + (py - 8) * s, (py - 8) * c - (px + 4) * s];
      const head = (1 - Math.hypot(qx / 5, qy / 3.8)) * 5;
      const stem = py > -13 && py < 8 ? 1.2 - Math.abs(px - 0.5) : 0;
      return Math.max(head, stem, 1.4 - toLine(px, py, FLAG));
    },
  },
  /** A folded paper bird, the fold down its middle. */
  paper: {
    w: 32,
    h: 32,
    turn: 'flip',
    solid: true,
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 32);
      const inside = py < 10 && Math.abs(px) < (py + 13) * (14 / 23);
      return inside ? (px < 0 ? 0.78 : 1) : 0;
    },
  },
  /** A blob of gloop, wobbling. */
  blob: { w: 32, h: 32, turn: 'up', solid: true, squash: true, a: (x, y) => 12 - polar(x, y, 32, 32).r },
  /** A little cloud, flat underneath. */
  cloud: {
    w: 32,
    h: 24,
    turn: 'up',
    solid: true,
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 24);
      const lump = Math.max(
        7 - Math.hypot(px + 7, py - 3),
        9 - Math.hypot(px - 1, py + 2),
        6.5 - Math.hypot(px - 8, py - 3),
      );
      return Math.min(lump, 8.5 - py);
    },
  },
  /** A gold coin, a groove round its face. */
  coin: {
    w: 32,
    h: 32,
    turn: 'flip',
    solid: true,
    a: (x, y) => {
      const { r } = polar(x, y, 32, 32);
      return unit(12.5 - r) * (r > 8.5 && r < 10 ? 0.65 : 1);
    },
  },
  /** A sparkle: four points round a bright middle. */
  sparkle: {
    w: 32,
    h: 32,
    turn: 'up',
    a: (x, y) => {
      const { px, py, r } = polar(x, y, 32, 32);
      const [dx, dy] = [Math.abs(px), Math.abs(py)];
      return Math.max(1.2 - dx / 3 - dy / 14, 1.2 - dy / 3 - dx / 14, 1.8 - r / 4);
    },
  },
  /** A fleck of pepper, lumpy. */
  speck: {
    w: 16,
    h: 16,
    turn: 'spin',
    solid: true,
    a: (x, y) => {
      const { r, t } = polar(x, y, 16, 16);
      return 5 + 1.2 * Math.sin(3 * t + 1) + 0.8 * Math.sin(5 * t + 2) - r;
    },
  },
  /** A spark of struck steel, stretched along its flight. */
  spark: {
    w: 32,
    h: 8,
    turn: 'way',
    stretch: true,
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 8);
      return (1 - Math.abs(px) / 16) * (1 - Math.abs(py) / 3.2) * 1.6;
    },
  },
  /** A sheriff's star, five points. */
  star5: {
    w: 32,
    h: 32,
    turn: 'spin',
    solid: true,
    a: (x, y) => {
      const { r, t } = polar(x, y, 32, 32);
      const k = ((((t + Math.PI / 2) / ((2 * Math.PI) / 5)) % 1) + 1) % 1;
      return (6 + 8.5 * Math.abs(1 - 2 * k) - r) * 1.5;
    },
  },
  /** A streamer of ribbon, waving. */
  ribbon: {
    w: 32,
    h: 12,
    turn: 'spin',
    solid: true,
    a: (x, y) => {
      const { px, py } = polar(x, y, 32, 12);
      return unit(1.7 - Math.abs(py - 2.8 * Math.sin((px / 16) * Math.PI * 1.3))) * unit((16 - Math.abs(px)) / 1.5);
    },
  },
} as const satisfies Record<string, MoteMask>;

export type MoteShape = keyof typeof MOTES;

/**
 * How a mote moves: `drift` an ember's (thrown off, wobbling, rising while fresh and sinking once spent); `float` up and
 * swaying, popping at the end (a bubble, a note); `flutter` down, swinging (a feather, paper, a ribbon); `drip` straight
 * down; `arc` thrown up and falling (a coin, a spark, pepper); `pop` out in a burst, then falling (confetti); `helix`
 * winding round its way (foxfire); `implode` sucked back into its way, shrinking (a vortex); `flash` there and gone
 * (lightning); `expand` where it fell, growing (a ripple, a cloud); `hover` stopping where it fell (a cog, a sparkle);
 * `jitter` snapping about (a glitch); `smoke` rising, swelling and curling.
 */
export type MoteMove =
  | 'drift'
  | 'float'
  | 'flutter'
  | 'drip'
  | 'arc'
  | 'pop'
  | 'helix'
  | 'implode'
  | 'flash'
  | 'expand'
  | 'hover'
  | 'jitter'
  | 'smoke';

/**
 * How a hot streak looks where it isn't a plain gun's (`EMBERS`): the motes it sheds off its way, each a `mote` moving
 * as `move` says, glowing through `ramp` from fresh to spent (`mix`: one of them kept all its life, confetti); how hard
 * each lifts while fresh (`rise`, an ember's 1) and sinks (`fall`, px/s², an ember's 50 once spent: a drop falls at
 * once), how far out it flies across the way (`scatter`), how long it lasts, how big it is and how many come (`life`,
 * `size`, `rate`, ×), `burst`: that many at once, `blink`: it twinkles on and off. Its core is its colour lightened
 * unless `core`, its tip white unless `tip`.
 */
export interface StreakLook {
  ramp: readonly number[];
  mote: MoteShape;
  move?: MoteMove;
  mix?: boolean;
  rise?: number;
  fall?: number;
  scatter?: number;
  life?: number;
  size?: number;
  rate?: number;
  burst?: number;
  blink?: boolean;
  core?: number;
  tip?: number;
}

/** A streak's look with every knob set (`EMBERS` under a look's own). */
export type StreakStyle = Required<Omit<StreakLook, 'core' | 'tip'>> & Pick<StreakLook, 'core' | 'tip'>;

/** A plain gun's: embers cooling white-hot, gold, orange, ember red, rising while hot and sinking as they cool. */
export const EMBERS: StreakStyle = {
  ramp: [0xfff3c0, 0xfee761, 0xfeae34, 0xf77622, 0xb13e53],
  mote: 'dot',
  move: 'drift',
  mix: false,
  rise: 1,
  fall: 50,
  scatter: 1,
  life: 1,
  size: 1,
  rate: 1,
  burst: 1,
  blink: false,
};

/** Ramp `ramp` (0xrrggbb, first to last) `f` (0 … 1) of the way down. */
export function rampAt(ramp: readonly number[], f: number): number {
  const k = Math.max(0, Math.min(0.999, f)) * (ramp.length - 1);
  const i = Math.floor(k);
  const [a, b, t] = [ramp[i] as number, (ramp[i + 1] ?? ramp[i]) as number, k - i];
  const ch = (s: number) => Math.round(((a >> s) & 255) + (((b >> s) & 255) - ((a >> s) & 255)) * t);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

/** A mote on the go (world px, s). */
export interface Mote {
  look: StreakStyle;
  /** Its colour all its life (a `mix` look's), or -1: it glows down its ramp. */
  color: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** The way its bullet flew, and where on that way it fell off. */
  ux: number;
  uy: number;
  ox: number;
  oy: number;
  age: number;
  life: number;
  size: number;
  /** A wobble across that way (px/s², rad/s, phase), a flicker (rad/s, depth), how hard it lifts while fresh (px/s²). */
  wobble: number;
  beat: number;
  phase: number;
  flicker: number;
  depth: number;
  rise: number;
  /** How fast it turns (rad/s), and the last snap of a `jitter` one. */
  spin: number;
  snap: number;
}

/** A mote off a streak at (x, y) flying (ux, uy) at `speed`, `width` times the Pistol's, in `look`. */
export function spawnMote(
  look: StreakStyle,
  x: number,
  y: number,
  ux: number,
  uy: number,
  speed: number,
  width: number,
  random: () => number,
): Mote {
  const r = random;
  const gauss = () => (r() + r() + r() - 1.5) / 1.5;
  const along = speed * (0.015 + r() * 0.09);
  const side = gauss() * 26 * look.scatter;
  let [vx, vy] = [ux * along - uy * side, uy * along + ux * side];
  if (look.move === 'pop') {
    const [a, s] = [r() * Math.PI * 2, (50 + r() * 70) * look.scatter];
    [vx, vy] = [Math.cos(a) * s, Math.sin(a) * s];
  }
  // Thrown up first (a coin, a spark, confetti).
  if (look.move === 'arc' || look.move === 'pop') vy -= (30 + r() * 60) * look.rise;
  const m: Mote = {
    look,
    color: look.mix ? (look.ramp[Math.floor(r() * look.ramp.length)] as number) : -1,
    x: x + gauss() * 1.2,
    y: y + gauss() * 1.2,
    vx,
    vy,
    ux,
    uy,
    ox: x,
    oy: y,
    age: 0,
    life: (0.16 + r() ** 2 * 0.6) * look.life,
    size: (1 + r() * 1.3) * width * look.size,
    wobble: (15 + r() * 50) * (r() < 0.5 ? -1 : 1),
    beat: 5 + r() * 12,
    phase: r() * Math.PI * 2,
    // A blinking one goes nearly out and back, slower.
    flicker: look.blink ? 7 + r() * 9 : 14 + r() * 30,
    depth: look.blink ? 0.5 + r() * 0.35 : 0.1 + r() * 0.45,
    rise: -(8 + r() * 34) * look.rise,
    spin: (2 + r() * 6) * (r() < 0.5 ? -1 : 1),
    snap: -1,
  };
  // Out to one side of its way, to be sucked back in.
  if (look.move === 'implode') {
    const out = (8 + r() * 10) * (r() < 0.5 ? -1 : 1) * look.scatter;
    [m.x, m.y] = [x - uy * out, y + ux * out];
  }
  return m;
}

/** A pseudo-random 0 … 1 from `n` (a `jitter` mote's snaps: the same every time, no draw from the game's randomness). */
const hash = (n: number) => {
  const s = Math.sin(n) * 43758.5453;
  return s - Math.floor(s);
};

/** Mote `m` `dt` s on: false once it has gone. */
export function stepMote(m: Mote, dt: number): boolean {
  m.age += dt;
  if (m.age >= m.life) return false;
  const f = m.age / m.life;
  const look = m.look;
  const drag = (k: number) => Math.exp(-k * dt);
  switch (look.move) {
    case 'float': {
      const d = drag(2.5);
      m.vx = m.vx * d + Math.sin(m.age * 3 + m.phase) * 40 * dt;
      m.vy = m.vy * d + m.rise * dt;
      break;
    }
    case 'flutter': {
      const d = drag(2.2);
      m.vx = m.vx * d + Math.cos(m.age * 4 + m.phase) * 70 * dt;
      m.vy = m.vy * d + look.fall * dt;
      break;
    }
    case 'drip':
      m.vx *= drag(5);
      m.vy = (m.vy + look.fall * dt) * drag(0.4);
      break;
    case 'arc':
    case 'pop': {
      const pop = look.move === 'pop';
      m.vx *= drag(pop ? 2 : 0.8);
      m.vy = (m.vy + look.fall * dt) * drag(pop ? 1.2 : 0.2);
      break;
    }
    case 'helix': {
      const d = drag(2);
      m.vx *= d;
      m.vy = m.vy * d + m.rise * 0.5 * dt;
      break;
    }
    case 'implode': {
      const k = 1 - drag(5);
      m.x += (m.ox - m.x) * k;
      m.y += (m.oy - m.y) * k;
      return true;
    }
    case 'flash':
    case 'expand': {
      const d = drag(look.move === 'flash' ? 14 : 6);
      m.vx *= d;
      m.vy *= d;
      break;
    }
    case 'hover': {
      const d = drag(7);
      m.vx *= d;
      m.vy = m.vy * d + look.fall * 0.3 * dt;
      break;
    }
    case 'jitter': {
      const snap = Math.floor(m.age / 0.07);
      if (snap !== m.snap) {
        m.snap = snap;
        const reach = 12 * look.scatter;
        m.x = m.ox + (hash(snap * 12.9898 + m.phase * 78.233) - 0.5) * reach;
        m.y = m.oy + (hash(snap * 39.3468 + m.phase * 11.135) - 0.5) * reach;
      }
      return true;
    }
    case 'smoke': {
      const d = drag(1.6);
      m.vx = m.vx * d + Math.sin(m.age * 2 + m.phase) * 20 * dt;
      m.vy = m.vy * d + m.rise * dt;
      break;
    }
    default: {
      const d = drag(3.2);
      m.vx *= d;
      m.vy *= d;
      // A wobble across its way; it rises while hot and sinks as it cools.
      const across = Math.sin(m.age * m.beat + m.phase) * m.wobble * dt;
      m.vx -= m.uy * across;
      m.vy += m.ux * across;
      m.vy += (m.rise * (1 - f) + look.fall * f) * dt;
    }
  }
  m.x += m.vx * dt;
  m.y += m.vy * dt;
  return true;
}

/** How a mote is drawn now: its mask's middle at (x, y), turned, `w` × `h` world px, in `alpha` and `tint`. */
export interface MoteView {
  shape: MoteShape;
  x: number;
  y: number;
  rotation: number;
  w: number;
  h: number;
  alpha: number;
  tint: number;
  /** Drawn as it is, not added on as light. */
  solid: boolean;
}

/** Mote `m` as drawn now, written into `out`. */
export function viewMote(m: Mote, out: MoteView): MoteView {
  const look = m.look;
  const mask: MoteMask = MOTES[look.mote];
  const f = m.age / m.life;
  const flicker = 1 - m.depth * (0.5 + 0.5 * Math.sin(m.age * m.flicker + m.phase));
  const fadeIn = Math.min(1, f / 0.06);
  let alpha = fadeIn * (1 - f) ** 0.7 * flicker;
  let grow = 1 - 0.55 * f;
  let [x, y] = [m.x, m.y];
  switch (look.move) {
    case 'float':
      // Popping at the end.
      grow = f > 0.85 ? 1 + (f - 0.85) * 3 : 1;
      alpha = fadeIn * (f > 0.85 ? (1 - f) / 0.15 : 1) * flicker;
      break;
    case 'expand':
      grow = 0.35 + 1.3 * f;
      alpha = Math.min(1, f / 0.04) * (1 - f) ** 1.3 * flicker;
      break;
    case 'smoke':
      grow = 0.7 + 1.5 * f;
      alpha = Math.min(1, f / 0.1) * (1 - f) ** 1.4 * 0.8 * flicker;
      break;
    case 'implode':
      grow = (1 - f) ** 0.6;
      break;
    case 'flash':
      grow = 1;
      alpha = (Math.sin(m.age * 70 + m.phase * 9) > -0.3 ? 1 : 0.2) * (1 - f) ** 0.5;
      break;
    case 'helix': {
      const reach = (2 + 7 * f) * look.scatter * Math.sin(m.age * m.spin * 1.5 + m.phase);
      x -= m.uy * reach;
      y += m.ux * reach;
      break;
    }
    case 'jitter':
      [x, y, grow] = [Math.round(x), Math.round(y), 1];
      break;
    case 'flutter':
    case 'hover':
    case 'arc':
    case 'pop':
    case 'drip':
      grow = 1 - 0.3 * f;
      break;
  }
  const big = Math.max(mask.w, mask.h);
  const size = m.size * grow;
  let [w, h] = [(size * mask.w) / big, (size * mask.h) / big];
  const speed = Math.hypot(m.vx, m.vy);
  let rotation = 0;
  if (mask.turn === 'way') {
    rotation = speed > 1 ? Math.atan2(m.vy, m.vx) : Math.atan2(m.uy, m.ux);
    if (mask.stretch) w *= 1 + Math.min(2, speed / 40);
  } else if (mask.turn === 'spin') rotation = m.phase + m.age * m.spin;
  else if (mask.turn === 'flip') {
    rotation = m.phase * 0.5;
    w *= Math.max(0.15, Math.abs(Math.cos(m.age * m.spin * 1.5 + m.phase)));
  }
  // A feather or a ribbon swings as it falls; lightning stays as it struck.
  if (look.move === 'flutter' && mask.turn !== 'flip') rotation = m.phase * 0.3 + Math.sin(m.age * 4 + m.phase) * 0.9;
  if (look.move === 'flash') rotation = m.phase;
  if (mask.squash) {
    const q = Math.sin(m.age * 14 + m.phase) * 0.18;
    [w, h] = [w * (1 + q), h * (1 - q)];
  }
  out.shape = look.mote;
  out.x = x;
  out.y = y;
  out.rotation = rotation;
  out.w = w;
  out.h = h;
  out.alpha = alpha;
  out.tint = m.color >= 0 ? m.color : rampAt(look.ramp, f);
  out.solid = mask.solid === true;
  return out;
}
